import React, { useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    CardMedia,
    Input,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";

const AddAdBanner = () => {
    const navigate = useNavigate();

    const [image, setImage] = useState<string>("");
    const [fileName, setFileName] = useState<string>("");

    // Form states
    const [geoLocation, setGeoLocation] = useState("default");
    const [vendor, setVendor] = useState("default");
    const [duration, setDuration] = useState("default");
    const [validityDays, setValidityDays] = useState("default");
    const [discountType, setDiscountType] = useState("default");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    return (
        <>
            <div className="main_title">
                <h1>Add Advertisement Banner</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/ad-banner")}>Manage Ad Banners</button> - Add Advertisement
                </p>
            </div>

            <div className="cards">
                <div className="form">
                    <div className="gap_p">
                        {/* ===== Upload Banner Image ===== */}
                        <div className="control_group w_100">
                            <label htmlFor="bannerImage">
                                Upload Banner Image <span className="req">*</span>
                            </label>
                            {image ? (
                                <div className="upload_image">
                                    <div className="upload_image_holder no_radius">
                                        <figure>
                                            <CardMedia component="img" image={image} alt="Banner" />
                                        </figure>
                                        <CloseIcon
                                            onClick={() => {
                                                setImage("");
                                                setFileName("");
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <label className="upload_image" htmlFor="bannerImage">
                                    <Input
                                        sx={{ display: "none" }}
                                        id="bannerImage"
                                        type="file"
                                        inputProps={{ accept: "image/png,image/jpeg" }}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            const files = event.target.files;
                                            if (files && files[0].type.includes("image")) {
                                                setFileName(files[0].name);
                                                setImage(URL.createObjectURL(files[0]));
                                            }
                                        }}
                                    />
                                    <span className="upload_image_holder no_radius">
                                        <figure className="add_img">
                                            <img src="/static/images/placeholder.png" alt="placeholder" />
                                            <span className="upload_img_icon">
                                                <img src="/static/images/edit2_icon.svg" alt="edit icon" />
                                            </span>
                                        </figure>
                                    </span>
                                </label>
                            )}
                        </div>

                        {/* ===== Advertisement Title ===== */}
                        <div className="control_group w_50">
                            <label htmlFor="adTitle">
                                Advertisement Title <span className="req">*</span>
                            </label>
                            <TextField
                                id="adTitle"
                                placeholder="Enter Ad Title"
                                fullWidth
                            />
                        </div>

                        {/* ===== Vendor ===== */}
                        <div className="control_group w_50">
                            <label htmlFor="vendorSelect">
                                Vendor <span className="req">*</span>
                            </label>
                            <Select
                                id="vendorSelect"
                                fullWidth
                                value={vendor}
                                onChange={(e: SelectChangeEvent) =>
                                    setVendor(e.target.value as string)
                                }
                            >
                                <MenuItem disabled value="default">
                                    Select Vendor
                                </MenuItem>
                                <MenuItem value="vendorA">Vendor A</MenuItem>
                                <MenuItem value="vendorB">Vendor B</MenuItem>
                                <MenuItem value="vendorC">Vendor C</MenuItem>
                            </Select>
                        </div>

                        {/* ===== Validity and Schedule ===== */}
                        <div className="title_inner">
                            <h2>Validity and Schedule</h2>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="startDateTime">
                                Start Date & Time <span className="req">*</span>
                            </label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={startDate}
                                    onChange={(val) => setStartDate(val)}
                                    slotProps={{
                                        textField: { id: "startDateTime", fullWidth: true },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="endDateTime">
                                End Date & Time <span className="req">*</span>
                            </label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={endDate}
                                    onChange={(val) => setEndDate(val)}
                                    slotProps={{
                                        textField: { id: "endDateTime", fullWidth: true },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="durationSelect">Duration Per Day</label>
                            <Select
                                id="durationSelect"
                                fullWidth
                                value={duration}
                                onChange={(e: SelectChangeEvent) =>
                                    setDuration(e.target.value as string)
                                }
                            >
                                <MenuItem disabled value="default">
                                    Select Hours
                                </MenuItem>
                                <MenuItem value="2">2 Hours</MenuItem>
                                <MenuItem value="4">4 Hours</MenuItem>
                                <MenuItem value="8">8 Hours</MenuItem>
                            </Select>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="validitySelect">Total Validity (Days)</label>
                            <Select
                                id="validitySelect"
                                fullWidth
                                value={validityDays}
                                onChange={(e: SelectChangeEvent) =>
                                    setValidityDays(e.target.value as string)
                                }
                            >
                                <MenuItem disabled value="default">
                                    Select Days
                                </MenuItem>
                                <MenuItem value="7">7 Days</MenuItem>
                                <MenuItem value="15">15 Days</MenuItem>
                                <MenuItem value="30">30 Days</MenuItem>
                            </Select>
                        </div>

                        {/* ===== Assign Products ===== */}
                        <div className="control_group w_100">
                            <label htmlFor="productSelect">Assign Products to Banner</label>
                            <Select id="productSelect" fullWidth value="default">
                                <MenuItem disabled value="default">
                                    Select Products
                                </MenuItem>
                                <MenuItem value="product1">Product 1</MenuItem>
                                <MenuItem value="product2">Product 2</MenuItem>
                                <MenuItem value="product3">Product 3</MenuItem>
                            </Select>
                        </div>

                        {/* ===== Discount Settings ===== */}
                        <div className="title_inner">
                            <h2>Discount Settings</h2>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="discountTypeSelect">Discount Type</label>
                            <Select
                                id="discountTypeSelect"
                                fullWidth
                                value={discountType}
                                onChange={(e: SelectChangeEvent) =>
                                    setDiscountType(e.target.value as string)
                                }
                            >
                                <MenuItem disabled value="default">
                                    Select Discount Type
                                </MenuItem>
                                <MenuItem value="flat">Flat</MenuItem>
                                <MenuItem value="percentage">Percentage</MenuItem>
                            </Select>
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="maxDiscount">Maximum Discount</label>
                            <TextField id="maxDiscount" placeholder="e.g., 100" fullWidth />
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="minOrderValue">Minimum Order Value</label>
                            <TextField id="minOrderValue" placeholder="e.g., 500" fullWidth />
                        </div>

                        <div className="control_group w_25">
                            <label htmlFor="productLimit">Product Limit for Discount</label>
                            <TextField id="productLimit" placeholder="e.g., 3" fullWidth />
                        </div>
                    </div>

                    {/* ===== Save Button ===== */}
                    <div className="form_btn">
                        <Button className="btn btn_primary">Save</Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddAdBanner;
